/**
 * `mm:ss`, and `h:mm:ss` once an hour is on the clock.
 *
 * Always padded and always tabular: a duration column is read by comparing the
 * same digit position down the rows, and `9:5` breaks that alignment.
 *
 * One shape for the whole app: the same call read `3:45` in Llamadas, `3 min
 * 45 s` on its own card in Actividades, and `65:00` in Contactos, where the
 * local helper had no hours at all.
 */
export function formatDuration(totalSeconds: number): string {
  const safe = Number.isFinite(totalSeconds) ? Math.max(0, Math.floor(totalSeconds)) : 0;
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  const pad = (value: number) => String(value).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}

/**
 * Spoken form, for screen readers and tooltips: «3 minutos y 42 segundos».
 *
 * Both halves agree with their number. «1 segundos» in a tooltip that repeats
 * on every transcript line is the kind of detail that makes a product read as
 * machine-generated, and a screen reader says it out loud.
 */
export function spellDuration(totalSeconds: number): string {
  const safe = Math.max(0, Math.round(totalSeconds));
  const seconds = safe % 60;
  const tail = seconds === 1 ? '1 segundo' : `${seconds} segundos`;
  if (safe < 60) return tail;
  const minutes = Math.floor(safe / 60);
  const head = minutes === 1 ? '1 minuto' : `${minutes} minutos`;
  return seconds === 0 ? head : `${head} y ${tail}`;
}
